import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { supabase } from "@/integrations/supabase/client";
import { checkTaskStatus } from "@/lib/task-checker";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
    Video,
    Image as ImageIcon,
    Sparkles,
    Loader2,
    Download,
    Coins,
    History,
    RefreshCw,
    Trash2,
    Copy,
    Upload,
    FastForward,
    Stamp,
    AtSign,
    Zap,
    X,
    LayoutGrid,
    Repeat,
    Layers
} from "lucide-react";

const Grok = () => {
    const { type } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();
    const { user, profile, loading: authLoading } = useAuth();
    const { toast } = useToast();
    const { generate, isGenerating, generationProgress, statusMessage } = useGeneration();

    const [activeTab, setActiveTab] = useState(type || "img-to-vdo");
    const [promptText, setPromptText] = useState("");
    const [selectedRatio, setSelectedRatio] = useState("9:16");
    const [selectedMode, setSelectedMode] = useState("normal");
    const [selectedDuration, setSelectedDuration] = useState("10");

    // Image reference states
    const [referenceImage, setReferenceImage] = useState<File | null>(null);
    const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
    const [taskId, setTaskId] = useState("");
    const [index, setIndex] = useState(0);

    const [recentLogs, setRecentLogs] = useState<any[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

    // Gallery state
    const [showGallery, setShowGallery] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState<{ id: string; url: string; prompt: string }[]>([]);
    const [loadingGallery, setLoadingGallery] = useState(false);

    // Continue without waiting states
    const [allowNewGeneration, setAllowNewGeneration] = useState(false);
    const [showContinueButton, setShowContinueButton] = useState(false);

    const grokModels = {
        "img-to-vdo": { id: "grok-imagine/image-to-video", name: "Grok Image To Video", credits: 30 },
        "text-to-vdo": { id: "grok-imagine/text-to-video", name: "Grok Text To Video", credits: 35 },
        "img-to-img": { id: "grok-imagine/image-to-image", name: "Grok Image To Image", credits: 5 },
        "text-to-img": { id: "grok-imagine/text-to-image", name: "Grok Text To Image", credits: 5 },
    };

    useEffect(() => {
        if (type && type !== activeTab) {
            setActiveTab(type);
        }
    }, [type]);

    useEffect(() => {
        if (user) {
            fetchRecentLogs();
        }
    }, [user, activeTab]);

    const handleTabChange = (val: string) => {
        setActiveTab(val);
        navigate(`/generate/grok/${val}`, { replace: true });
    };

    const fetchRecentLogs = async () => {
        if (!user) return;
        setLoadingLogs(true);
        try {
            const { data, error } = await supabase
                .from("generation_logs")
                .select("*")
                .eq("user_id", user.id)
                .ilike("model", "%grok%")
                .order("created_at", { ascending: false })
                .limit(10);

            if (error) throw error;
            setRecentLogs(data || []);
        } catch (err) {
            console.error("Error fetching logs:", err);
        } finally {
            setLoadingLogs(false);
        }
    };

    const fetchGalleryPhotos = async () => {
        if (!user) return;
        setLoadingGallery(true);
        try {
            const { data, error } = await supabase
                .from("generation_logs")
                .select("id, prompt, result_url")
                .eq("user_id", user.id)
                .eq("service_type", "photo")
                .eq("status", "completed")
                .not("result_url", "is", null)
                .order("created_at", { ascending: false })
                .limit(24);

            if (error) throw error;

            const photos = (data || []).map(item => {
                let url = item.result_url || "";
                if (url.startsWith("{")) {
                    try {
                        const parsed = JSON.parse(url);
                        url = parsed.resultUrls?.[0] || parsed.url || url;
                    } catch { }
                }
                return {
                    id: item.id,
                    url: url,
                    prompt: item.prompt || ""
                };
            }).filter(p => p.url.startsWith("http"));

            setGalleryPhotos(photos);
        } catch (err) {
            console.error("Error fetching gallery photos:", err);
        } finally {
            setLoadingGallery(false);
        }
    };

    const handleSelectFromGallery = (url: string) => {
        setReferenceImageUrl(url);
        setReferenceImage(null);
        setShowGallery(false);
    };

    const getRequiredCredits = () => {
        const model = grokModels[activeTab as keyof typeof grokModels];
        if (!model) return 0;

        // For video generation, 720p: 3 credits/sec
        if (activeTab === "img-to-vdo" || activeTab === "text-to-vdo") {
            return parseInt(selectedDuration) * 3;
        }

        // For image generation, use base credits
        return model.credits;
    };

    const handleGenerate = async () => {
        if (isGenerating && !allowNewGeneration) return;

        // Validation
        if (!promptText.trim() && (activeTab === "img-to-vdo" || activeTab === "text-to-vdo" || activeTab === "img-to-img" || activeTab === "text-to-img")) {
            // For text-to-img and text-to-vdo prompt is required
        }

        if ((activeTab === "img-to-vdo" || activeTab === "img-to-img") && !referenceImage && !referenceImageUrl && !taskId) {
            toast({ variant: "destructive", title: t("กรุณาอัปโหลดรูปภาพ", "Please upload an image") });
            return;
        }

        setAllowNewGeneration(false);
        setShowContinueButton(true);

        const credits = getRequiredCredits();
        const model = grokModels[activeTab as keyof typeof grokModels]?.id;

        let options: any = {
            mode: selectedMode,
            duration: selectedDuration,
            ratio: selectedRatio,
        };

        if (activeTab === "img-to-vdo" || activeTab === "img-to-img") {
            if (referenceImage || referenceImageUrl) {
                options.referenceImage = referenceImage || referenceImageUrl;
            } else if (taskId) {
                options.task_id = taskId;
                options.index = index;
            }
        }

        const result = await generate(
            (activeTab === "img-to-vdo" || activeTab === "text-to-vdo") ? "video" : "photo",
            model || "",
            promptText,
            credits,
            options
        );

        setShowContinueButton(false);

        if (result.success) {
            fetchRecentLogs();
            setTimeout(() => {
                const historySection = document.getElementById('recent-history-section');
                if (historySection) {
                    historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);

            toast({
                title: t("กำลังสร้าง...", "Generating..."),
                description: t("คุณสามารถดูสถานะได้ที่ประวัติด้านล่าง", "Check status in history section below"),
            });
        }
    };

    const handleContinueWithoutWaiting = () => {
        setShowContinueButton(false);
        setAllowNewGeneration(true);
        toast({
            title: t("สร้างต่อได้เลย!", "You can continue!"),
            description: t("ผลลัพธ์ก่อนหน้าจะเก็บไว้ที่หน้า Dashboard", "Previous result will be saved to Dashboard"),
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setReferenceImage(e.target.files[0]);
            setReferenceImageUrl(null);
            setTaskId("");
        }
    };

    const handleCheckStatus = async (logId: string) => {
        setCheckingStatus(logId);
        try {
            const result = await checkTaskStatus(logId);
            if (result.success && result.status === "completed") {
                fetchRecentLogs();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setCheckingStatus(null);
        }
    };

    const formatDateHistory = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('th-TH', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const handleDeleteHistory = async (id: string) => {
        try {
            if (!confirm(t("คุณแน่ใจหรือไม่ที่จะลบรายการนี้?", "Are you sure you want to delete this item?"))) {
                return;
            }

            const { data: success, error } = await supabase.rpc('delete_generation_log' as any, { p_log_id: id });

            if (error) {
                const { error: dbError } = await supabase
                    .from("generation_logs")
                    .delete()
                    .eq("id", id);
                if (dbError) throw dbError;
            }

            toast({
                title: t("ลบสำเร็จ", "Deleted successfully"),
                description: t("รายการถูกลบออกจากประวัติแล้ว", "The item has been removed from history"),
            });

            fetchRecentLogs();
        } catch (error: any) {
            toast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive",
            });
        }
    };

    const handleReusePrompt = (prompt: string, model: string) => {
        let targetTab = "text-to-vdo";
        if (model.includes("image-to-video")) targetTab = "img-to-vdo";
        else if (model.includes("image-to-image")) targetTab = "img-to-img";
        else if (model.includes("text-to-image")) targetTab = "text-to-img";

        setPromptText(prompt);
        if (activeTab !== targetTab) {
            handleTabChange(targetTab);
        }
        window.scrollTo(0, 0);
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const renderStatusBadge = (status: string) => {
        switch (status) {
            case "completed":
                return <Badge className="bg-green-500">{t("สำเร็จ", "Completed")}</Badge>;
            case "processing":
                return <Badge className="bg-blue-500 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>;
            case "failed":
                return <Badge variant="destructive">{t("ล้มเหลว", "Failed")}</Badge>;
            default:
                return <Badge variant="outline">{t("รอดำเนินการ", "Pending")}</Badge>;
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Navbar />
            {!user && <GuestOverlay />}

            <main className="pt-24 pb-16 container mx-auto px-4 max-w-6xl">
                <div className="flex flex-col items-center mb-10 text-center">
                    <Badge variant="outline" className="mb-4 py-1 px-4 text-primary border-primary/20 bg-primary/5">
                        <Sparkles className="h-3.5 w-3.5 mr-2" />
                        xAI Grok Imagine Professional
                    </Badge>
                    <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4 tracking-tight">
                        Grok <span className="text-gradient">Imagine</span>
                    </h1>
                    <p className="text-muted-foreground max-w-2xl text-lg">
                        {t("สัมผัสความทรงพลังของ Grok Imagine จาก xAI สร้างวิดีโอและรูปภาพคุณภาพสูงพร้อมความสร้างสรรค์ระดับสูงสุด", "Experience the power of xAI's Grok Imagine. Create high-quality videos and images with peak creativity.")}
                    </p>
                    <Button variant="outline" className="mt-4 gap-2" asChild>
                        <Link to="/generate/grokbulk">
                            <Layers className="h-4 w-4" />
                            {t("สร้างแบบ Bulk (หลายรายการ)", "Bulk Generation (Multiple Items)")}
                        </Link>
                    </Button>
                </div>

                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                        <TabsList className="grid w-full max-w-3xl grid-cols-4 h-12 p-1 bg-muted/50 backdrop-blur-sm border border-border/50">
                            <TabsTrigger value="img-to-vdo" className="transition-all duration-300">
                                <Video className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Img to Vdo</span>
                            </TabsTrigger>
                            <TabsTrigger value="text-to-vdo" className="transition-all duration-300">
                                <Zap className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Text to Vdo</span>
                            </TabsTrigger>
                            <TabsTrigger value="img-to-img" className="transition-all duration-300">
                                <ImageIcon className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Img to Img</span>
                            </TabsTrigger>
                            <TabsTrigger value="text-to-img" className="transition-all duration-300">
                                <Sparkles className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Text to Img</span>
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-8 items-start">
                        <div className="lg:col-span-8 space-y-6">
                            <TabsContent value={activeTab}>
                                <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-xl">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            {activeTab.includes("vdo") ? <Video className="h-5 w-5 text-primary" /> : <Sparkles className="h-5 w-5 text-primary" />}
                                            {activeTab === "img-to-vdo" && t("สร้างวิดีโอจากรูปภาพ", "Image to Video")}
                                            {activeTab === "text-to-vdo" && t("สร้างวิดีโอจากข้อความ", "Text to Video")}
                                            {activeTab === "img-to-img" && t("ปรับแต่งรูปภาพ", "Image to Image")}
                                            {activeTab === "text-to-img" && t("สร้างรูปภาพจากข้อความ", "Text to Image")}
                                        </CardTitle>
                                        <CardDescription>
                                            {activeTab === "img-to-vdo" && t("อัปโหลดรูปภาพเพื่อเปลี่ยนเป็นวิดีโอที่เคลื่อนไหวอย่างสมจริง", "Upload an image to transform it into a realistically animated video")}
                                            {activeTab === "text-to-vdo" && t("พิมพ์คำบรรยายเพื่อสร้างวิดีโอจากจินตนาการ", "Type a description to create a video from your imagination")}
                                            {activeTab === "img-to-img" && t("อัปโหลดรูปภาพและระบุพรอมต์เพื่อปรับแต่งหรือเปลี่ยนสไตล์", "Upload an image and specify a prompt to refine or restyle it")}
                                            {activeTab === "text-to-img" && t("สร้างรูปภาพคุณภาพสูงจากข้อความที่คุณต้องการ", "Generate high-quality images from the text you desire")}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {(activeTab === "img-to-vdo" || activeTab === "img-to-img") && (
                                            <div className="space-y-4">
                                                <label className="text-sm font-medium">{t("อัปโหลดรูปภาพ", "Upload Image")}</label>
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div
                                                        className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/30 aspect-square md:aspect-auto"
                                                        onClick={() => document.getElementById('grok-image-upload')?.click()}
                                                    >
                                                        {referenceImage ? (
                                                            <img src={URL.createObjectURL(referenceImage)} alt="Preview" className="max-h-40 rounded-lg shadow-md" />
                                                        ) : referenceImageUrl ? (
                                                            <img src={referenceImageUrl} alt="Preview" className="max-h-40 rounded-lg shadow-md" />
                                                        ) : (
                                                            <>
                                                                <div className="bg-primary/10 p-4 rounded-full mb-4">
                                                                    <Upload className="h-8 w-8 text-primary" />
                                                                </div>
                                                                <p className="text-sm text-center text-muted-foreground">
                                                                    {t("คลิกเพื่อเลือกไฟล์", "Click to choose file")} <br />
                                                                    <span className="text-xs text-muted-foreground/60">(JPG, PNG, WEBP max 10MB)</span>
                                                                </p>
                                                            </>
                                                        )}
                                                        <input id="grok-image-upload" type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                                    </div>
                                                    <div className="flex flex-col gap-3">
                                                        <Button
                                                            variant="outline"
                                                            className="w-full h-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                                                            onClick={() => {
                                                                setShowGallery(true);
                                                                fetchGalleryPhotos();
                                                            }}
                                                        >
                                                            <ImageIcon className="h-5 w-5" />
                                                            {t("เลือกจากคลังภาพ", "Select from Gallery")}
                                                        </Button>
                                                        {activeTab === "img-to-vdo" && (
                                                            <div className="p-4 rounded-xl bg-muted/30 border border-border/50 flex flex-col gap-2">
                                                                <label className="text-xs font-semibold opacity-70 uppercase tracking-wider">{t("หรือใช้ Task ID", "Or use Task ID")}</label>
                                                                <Input
                                                                    placeholder="Grok Task ID"
                                                                    value={taskId}
                                                                    onChange={(e) => setTaskId(e.target.value)}
                                                                    className="h-9 text-xs"
                                                                />
                                                                <div className="flex items-center gap-2">
                                                                    <label className="text-[10px] whitespace-nowrap">{t("Index (0-5):", "Index (0-5):")}</label>
                                                                    <Input
                                                                        type="number"
                                                                        min="0" max="5"
                                                                        value={index}
                                                                        onChange={(e) => setIndex(parseInt(e.target.value) || 0)}
                                                                        className="h-8 w-16 text-center text-xs"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <label className="text-sm font-bold">{t("คำบรรยาย (Prompt)", "Prompt")}</label>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 px-2 text-muted-foreground hover:text-destructive"
                                                    onClick={() => setPromptText("")}
                                                    disabled={!promptText}
                                                >
                                                    <X className="h-3.5 w-3.5 mr-1" />
                                                    {t("ล้าง", "Clear")}
                                                </Button>
                                            </div>
                                            <Textarea
                                                placeholder={t("ระบุรายละเอียดที่คุณต้องการ...", "Enter details here...")}
                                                value={promptText}
                                                onChange={(e) => setPromptText(e.target.value)}
                                                className="min-h-[120px] resize-none focus-visible:ring-primary shadow-inner text-lg p-4"
                                            />
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6">
                                            {activeTab.includes("vdo") && (
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">{t("โหมดการสร้าง (Mode)", "Generation Mode")}</label>
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {["normal", "fun", "spicy"].map((m) => (
                                                            <Button
                                                                key={m}
                                                                variant={selectedMode === m ? "default" : "outline"}
                                                                onClick={() => setSelectedMode(m)}
                                                                className="capitalize"
                                                            >
                                                                {m}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-4">
                                                {activeTab.includes("vdo") && (
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium">{t("ความยาว (Duration) — 720p: 3 Cr./วินาที", "Duration — 720p: 3 Cr./sec")}</label>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            {["6", "10", "15", "20", "25", "30"].map((d) => (
                                                                <Button
                                                                    key={d}
                                                                    variant={selectedDuration === d ? "default" : "outline"}
                                                                    onClick={() => setSelectedDuration(d)}
                                                                    size="sm"
                                                                >
                                                                    {d}s ({parseInt(d) * 3} Cr.)
                                                                </Button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {activeTab !== "img-to-img" && (
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium">{t("สัดส่วน (Aspect Ratio)", "Aspect Ratio")}</label>
                                                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                                            {["2:3", "3:2", "1:1", "9:16", "16:9"].map((r) => (
                                                                <Button
                                                                    key={r}
                                                                    variant={selectedRatio === r ? "default" : "outline"}
                                                                    onClick={() => setSelectedRatio(r)}
                                                                    size="sm"
                                                                >
                                                                    {r}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2 pt-2">
                                            <Button
                                                size="lg"
                                                className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300"
                                                onClick={handleGenerate}
                                                disabled={(isGenerating && !allowNewGeneration) || (!promptText.trim() && activeTab !== "img-to-vdo") || (activeTab === "img-to-vdo" && !promptText.trim() && !referenceImage && !referenceImageUrl && !taskId)}
                                            >
                                                {isGenerating && !allowNewGeneration ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}
                                                {t("สร้างเดี๋ยวนี้", "Generate Now")}
                                                <span className="ml-2 opacity-70 text-sm font-normal">({getRequiredCredits()} {t("เครดิต", "Credits")})</span>
                                            </Button>

                                            {isGenerating && showContinueButton && !allowNewGeneration && (
                                                <Button
                                                    variant="outline"
                                                    size="lg"
                                                    className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                                                    onClick={handleContinueWithoutWaiting}
                                                >
                                                    <FastForward className="h-5 w-5" />
                                                    {t("สร้างต่อไม่รอแล้ว", "Continue, don't wait")}
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </div>

                        <div className="lg:col-span-4 space-y-6">
                            {/* Current Task Status */}
                            {isGenerating && (
                                <Card className="border-primary/30 bg-primary/5 backdrop-blur-md shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-500">
                                    <div className="absolute top-0 right-0 w-32 h-32 -mr-12 -mt-12 bg-primary/10 rounded-full blur-3xl"></div>
                                    <CardContent className="pt-8 pb-8 flex flex-col items-center text-center relative z-10">
                                        <div className="relative mb-6">
                                            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
                                            <div className="bg-background/80 backdrop-blur-sm rounded-full p-4 border border-primary/20 shadow-xl relative">
                                                <Loader2 className="h-10 w-10 text-primary animate-spin" />
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-bold mb-2 text-foreground">{t("กำลังสร้าง...", "Generating...")}</h3>
                                        <div className="w-full space-y-3 px-2">
                                            <div className="flex justify-between items-end text-xs mb-1">
                                                <span className="font-bold text-primary/80 uppercase tracking-widest">{t("สถานะ", "Status")}</span>
                                                <span className="font-mono font-bold text-primary">{Math.round(generationProgress)}%</span>
                                            </div>
                                            <Progress value={generationProgress} className="h-2.5 bg-primary/10" />
                                            <div className="flex items-center justify-center gap-2 pt-2">
                                                <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
                                                <span className="text-xs font-medium text-foreground/70">{statusMessage}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Credit Info */}
                            <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-lg overflow-hidden group">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-md flex items-center justify-between">
                                        {t("เครดิตของคุณ", "Your Credits")}
                                        <Coins className="h-4 w-4 text-amber-500" />
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-baseline gap-2 mb-4">
                                        <span className="text-4xl font-bold text-gradient">{profile?.credits || 0}</span>
                                        <span className="text-muted-foreground text-sm uppercase tracking-wider font-medium">{t("คงเหลือ", "Remaining")}</span>
                                    </div>
                                    <Button variant="hero" size="sm" className="w-full rounded-full" onClick={() => navigate("/pricing")}>
                                        {t("เติมเครดิต", "Top Up")}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* History Section */}
                    <div className="mt-12" id="recent-history-section">
                        <Card className="rounded-3xl border border-border/50 shadow-xl bg-card/50 backdrop-blur-md p-6 md:p-8">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
                                        <History className="h-6 w-6 text-primary" />
                                        {t("ประวัติการสร้าง Grok", "Grok History")}
                                    </h2>
                                    <p className="text-sm text-muted-foreground">{t("ผลลัพธ์ที่คุณสร้างด้วย Grok Imagine", "Your results generated with Grok Imagine")}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="icon" onClick={fetchRecentLogs} disabled={loadingLogs} className="rounded-full">
                                        <RefreshCw className={`h-5 w-5 ${loadingLogs ? "animate-spin" : ""}`} />
                                    </Button>
                                </div>
                            </div>

                            {loadingLogs ? (
                                <div className="flex flex-col items-center justify-center py-20 opacity-50">
                                    <Loader2 className="h-10 w-10 animate-spin mb-4" />
                                    <p>{t("กำลังโหลด...", "Loading...")}</p>
                                </div>
                            ) : recentLogs.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {recentLogs.map((log) => {
                                        const isCompleted = log.status === "completed";
                                        const isFailed = log.status === "failed";
                                        const isProcessing = log.status === "processing";
                                        const resultUrl = log.result_url;

                                        return (
                                            <Card key={log.id} className="group overflow-hidden border border-border/40 shadow-lg bg-background/60 backdrop-blur-sm hover:shadow-2xl transition-all duration-500 rounded-3xl flex flex-col h-full">
                                                <div className="p-3">
                                                    <div className={`relative rounded-2xl overflow-hidden bg-black border border-border/10 ${log.model.includes('video') ? 'aspect-[9/16]' : 'aspect-square'} flex items-center justify-center`}>
                                                        {isCompleted && resultUrl ? (
                                                            log.model.includes('video') ? (
                                                                <video src={resultUrl} className="w-full h-full object-contain" controls playsInline />
                                                            ) : (
                                                                <img src={resultUrl} alt="Result" className="w-full h-full object-contain" />
                                                            )
                                                        ) : (
                                                            <div className="flex flex-col items-center gap-2 px-3">
                                                                {isFailed ? <X className="text-destructive h-8 w-8" /> : <Loader2 className="h-8 w-8 animate-spin text-primary/30" />}
                                                                <span className="text-[10px] font-bold uppercase tracking-widest opacity-50">{log.status}</span>
                                                                {isFailed && log.error_message && (
                                                                    <p className="text-[10px] text-destructive/80 text-center line-clamp-3 px-2">
                                                                        {log.error_message}
                                                                    </p>
                                                                )}
                                                                {isProcessing && (
                                                                    <Button variant="outline" size="sm" onClick={() => handleCheckStatus(log.id)} className="h-7 text-[10px] px-2 rounded-full">
                                                                        {t("เช็กสถานะ", "Check")}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <CardContent className="p-5 pt-0 flex-1 flex flex-col">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-[9px] font-bold uppercase tracking-widest text-primary/70">{log.model.split('/').pop()}</span>
                                                        {renderStatusBadge(log.status)}
                                                    </div>
                                                    <p className="text-xs line-clamp-2 italic mb-4 flex-1">"{log.prompt || "No prompt"}"</p>
                                                    <div className="flex items-center justify-between pt-3 border-t border-border/20">
                                                        <span className="text-[10px] text-muted-foreground">{formatDateHistory(log.created_at)}</span>
                                                        <div className="flex gap-1">
                                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleReusePrompt(log.prompt, log.model)}><Repeat className="h-3.5 w-3.5" /></Button>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteHistory(log.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                                            {isCompleted && resultUrl && (
                                                                <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => handleDownload(resultUrl, `grok-${log.id.slice(0, 8)}`)}><Download className="h-3.5 w-3.5" /></Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                    <History className="h-12 w-12 mb-2" />
                                    <p>{t("ยังไม่มีประวัติ", "No history yet")}</p>
                                </div>
                            )}
                        </Card>
                    </div>
                </Tabs>
            </main>

            {/* Gallery Modal (Simplified) */}
            {showGallery && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowGallery(false)}>
                    <div className="bg-background rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b flex justify-between">
                            <h3 className="text-xl font-bold">{t("แกลเลอรีของคุณ", "Your Gallery")}</h3>
                            <Button variant="ghost" size="icon" onClick={() => setShowGallery(false)}><X className="h-5 w-5" /></Button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {loadingGallery ? <Loader2 className="animate-spin" /> : galleryPhotos.map(p => (
                                <img key={p.id} src={p.url} className="aspect-square object-cover rounded-lg cursor-pointer hover:ring-2 ring-primary" onClick={() => handleSelectFromGallery(p.url)} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Grok;
